import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { ManageDeliveryPage } from "@/features/script-delivery/pages/ManageDeliveryPage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import { pageStateQueryOptions, PageAccessError, orderStatusTimeDashboardQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/order-status-time")({
  beforeLoad: () => {
    assertPagePermission("order-status-time");
    const { tenants, activeTenantId } = useAuth.getState();
    const activeTenant = tenants.find((tenant) => tenant.id === activeTenantId);
    if (activeTenant && activeTenant.type !== "SUPER_TENANT") {
      throw new PageAccessError("order-status-time");
    }
  },
  loader: async ({ context }) => {
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("order-status-time"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });

    void context.queryClient.prefetchQuery(orderStatusTimeDashboardQueryOptions());

    return pageState;
  },
  component: OrderStatusTimeRoute,
  errorComponent: PageAccessErrorComponent,
});

function OrderStatusTimeRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.scriptDelivery"));
  const { isLoading, error } = usePageState("order-status-time");

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        {t("orderStatusTime.loading", "Loading…")}
      </div>
    );
  }

  if (error) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  return <ManageDeliveryPage includeScriptDelivery={false} />;
}
